import { Boundaries } from "@planzone/core";
import { editMilestoneError } from "@planzone/dto";
import { events } from "@planzone/events";
import { createStateReducer } from "../../utils/createStateReducer";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRequest(
  state: State,
  { milestone }: Boundaries["editMilestone"]["request"],
): State {
  const index = state.findIndex((res) => res.milestone.uuid === milestone.uuid);

  return index >= 0
    ? [
        ...state.slice(0, index),
        {
          request: setRequestStateToPending(),
          milestone,
          error: editMilestoneError,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          request: setRequestStateToPending(),
          milestone,
          error: editMilestoneError,
        },
      ];
}

function handleSuccess(
  state: State,
  { milestone, impactedElements }: Boundaries["editMilestone"]["success"],
): State {
  return state.map((req) => {
    const impactedElement = impactedElements.find(
      ({ uuid }) => req.milestone.uuid === uuid,
    );

    return req.milestone.uuid === milestone.uuid
      ? {
          request: setRequestStateToSuccess(),
          milestone,
          error: editMilestoneError,
        }
      : !!impactedElement
        ? {
            request: setRequestStateToSuccess(),
            milestone: { ...req.milestone, ...impactedElement },
            error: editMilestoneError,
          }
        : req;
  });
}

function handleFailure(
  state: State,
  { milestone, error }: Boundaries["editMilestone"]["failure"],
): State {
  return state.map((res) =>
    res.milestone.uuid === milestone.uuid
      ? {
          request: setRequestStateToFailure(),
          milestone,
          error,
        }
      : res,
  );
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.editMilestoneRequested]: handleRequest,
    [events.editMilestoneSucceeded]: handleSuccess,
    [events.editMilestoneFailed]: handleFailure,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
